import React, { useState, memo } from 'react';
import { MapPin, Lock, Unlock, Eye, User, Package, Trash2, RefreshCw } from 'lucide-react';
import { StcIntRelOrderDto } from '../../../../types/putaway';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PutAwayCardActionsProps {
  order: StcIntRelOrderDto;
  isOwned: boolean;
  /** Låst av en annen operatør — vises kun for lagersjef/administrator (profilgruppe 2/3) */
  isLockedByOther?: boolean;
  /** Lagersjef/administrator (profilgruppe 2/3) — viser slett/regenerer på ulåste ordrer */
  isManagerOrAdmin?: boolean;
  onStartPutAway: (order: StcIntRelOrderDto) => Promise<void>;
  onLockOrder: (stcOrderNo: number) => Promise<void>;
  onUnlockOrder: (stcOrderNo: number) => Promise<void>;
  onPreviewOrder: (stcOrderNo: number) => void;
  onDeleteOrder?: (order: StcIntRelOrderDto) => Promise<void>;
  onRegenerateOrder?: (order: StcIntRelOrderDto) => Promise<void>;
}

// Separate component for actions to isolate loading states
export const PutAwayCardActions: React.FC<PutAwayCardActionsProps> = memo(({
  order,
  isOwned,
  isLockedByOther = false,
  isManagerOrAdmin = false,
  onStartPutAway,
  onLockOrder,
  onUnlockOrder,
  onPreviewOrder,
  onDeleteOrder,
  onRegenerateOrder
}) => {
  const { t } = useTranslation();
  const [startingOrder, setStartingOrder] = useState(false);
  const [lockingOrder, setLockingOrder] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [regeneratingOrder, setRegeneratingOrder] = useState(false);

  const handleStartPutAway = async () => {
    try {
      setStartingOrder(true);
      await onStartPutAway(order);
    } catch (err) {
      console.error('Error starting put-away:', err);
    } finally {
      setStartingOrder(false);
    }
  };

  const handleLockOrder = async () => {
    try {
      setLockingOrder(true);
      await onLockOrder(order.stcOrderNo);
    } catch (err) {
      console.error('Error locking order:', err);
    } finally {
      setLockingOrder(false);
    }
  };

  const handleUnlockOrder = async () => {
    try {
      setLockingOrder(true);
      await onUnlockOrder(order.stcOrderNo);
    } catch (err) {
      console.error('Error unlocking order:', err);
    } finally {
      setLockingOrder(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!onDeleteOrder) return;
    try {
      setDeletingOrder(true);
      await onDeleteOrder(order);
    } catch (err) {
      console.error('Error deleting order:', err);
    } finally {
      setDeletingOrder(false);
    }
  };

  const handleRegenerateOrder = async () => {
    if (!onRegenerateOrder) return;
    try {
      setRegeneratingOrder(true);
      await onRegenerateOrder(order);
    } catch (err) {
      console.error('Error regenerating order:', err);
    } finally {
      setRegeneratingOrder(false);
    }
  };

  // Slett/regenerer (kun lagersjef/administrator, kun ulåste ordrer — låste må frigis først,
  // jf. backend som avviser låste ordrer med 409)
  const showManagerActions =
    isManagerOrAdmin && !isLockedByOther && !isOwned && !!onDeleteOrder && !!onRegenerateOrder;

  const managerActionButtons = showManagerActions ? (
    <>
      <button
        onClick={handleRegenerateOrder}
        disabled={regeneratingOrder || deletingOrder}
        title={t('warehouse.modules.putaway.actions.regenerateTooltip', {}, 'Slett og generer ordren på nytt fra grunnlag')}
        className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1.5 rounded text-xs hover:bg-blue-200 transition-colors disabled:opacity-50 min-w-[40px] min-h-[36px] sm:min-w-[50px] sm:min-h-[40px] justify-center"
      >
        {regeneratingOrder ? (
          <div className="w-2.5 h-2.5 animate-spin border border-blue-500 border-t-transparent rounded-full" />
        ) : (
          <>
            <RefreshCw size={12} className="sm:w-3 sm:h-3" />
            <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.regenerate', {}, 'Regenerer')}</span>
            <span className="sm:hidden">↻</span>
          </>
        )}
      </button>

      <button
        onClick={handleDeleteOrder}
        disabled={deletingOrder || regeneratingOrder}
        title={t('warehouse.modules.putaway.actions.deleteTooltip', {}, 'Slett ordre og legg grunnlaget tilbake')}
        className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1.5 rounded text-xs hover:bg-red-200 transition-colors disabled:opacity-50 min-w-[40px] min-h-[36px] sm:min-w-[50px] sm:min-h-[40px] justify-center"
      >
        {deletingOrder ? (
          <div className="w-2.5 h-2.5 animate-spin border border-red-500 border-t-transparent rounded-full" />
        ) : (
          <>
            <Trash2 size={12} className="sm:w-3 sm:h-3" />
            <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.delete', {}, 'Slett')}</span>
            <span className="sm:hidden">🗑</span>
          </>
        )}
      </button>
    </>
  ) : null;

  // Utlegg låst av annen operatør (kun synlig for lagersjef/administrator):
  // ingen Start/Ta-knapp — leder skal frigi låsen, ikke overta arbeidet
  if (isLockedByOther) {
    return (
      <div className="flex gap-1 sm:gap-2">
        <button
          onClick={handleUnlockOrder}
          disabled={lockingOrder}
          className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1.5 rounded text-xs hover:bg-amber-200 transition-colors disabled:opacity-50 min-w-[50px] min-h-[36px] sm:min-w-[60px] sm:min-h-[40px] justify-center"
        >
          {lockingOrder ? (
            <div className="w-2.5 h-2.5 animate-spin border border-amber-600 border-t-transparent rounded-full" />
          ) : (
            <>
              <Unlock size={12} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.unlock')}</span>
              <span className="sm:hidden">🔓</span>
            </>
          )}
        </button>

        <button
          onClick={() => onPreviewOrder(order.stcOrderNo)}
          className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1.5 rounded text-xs hover:bg-gray-200 transition-colors min-w-[40px] min-h-[36px] sm:min-w-[50px] sm:min-h-[40px] justify-center"
        >
          <Eye size={12} className="sm:w-3 sm:h-3" />
          <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.view')}</span>
          <span className="sm:hidden">👁</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-1 sm:gap-2">
      <button
        onClick={handleStartPutAway}
        disabled={startingOrder}
        className="flex items-center gap-1 bg-green-600 text-white px-2 py-1.5 rounded text-xs hover:bg-green-700 transition-colors disabled:opacity-50 min-w-[50px] min-h-[36px] sm:min-w-[60px] sm:min-h-[40px] justify-center"
      >
        {startingOrder ? (
          <>
            <div className="w-2.5 h-2.5 animate-spin border border-white border-t-transparent rounded-full" />
          </>
        ) : (
          <>
            <MapPin size={12} className="sm:w-3 sm:h-3" />
            <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.start')}</span>
            <span className="sm:hidden">▶</span>
          </>
        )}
      </button>

      {isOwned ? (
        <button
          onClick={handleUnlockOrder}
          disabled={lockingOrder}
          className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1.5 rounded text-xs hover:bg-red-200 transition-colors disabled:opacity-50 min-w-[50px] min-h-[36px] sm:min-w-[60px] sm:min-h-[40px] justify-center"
        >
          {lockingOrder ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-red-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Unlock size={12} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.unlock')}</span>
              <span className="sm:hidden">🔓</span>
            </>
          )}
        </button>
      ) : (
        <button
          onClick={handleLockOrder}
          disabled={lockingOrder}
          className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1.5 rounded text-xs hover:bg-green-200 transition-colors disabled:opacity-50 min-w-[60px] min-h-[36px] sm:min-w-[80px] sm:min-h-[40px] justify-center"
        >
          {lockingOrder ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-green-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Lock size={12} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.takePutaway')}</span>
              <span className="sm:hidden">{t('warehouse.modules.putaway.actions.take')}</span>
            </>
          )}
        </button>
      )}

      <button
        onClick={() => onPreviewOrder(order.stcOrderNo)}
        className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1.5 rounded text-xs hover:bg-gray-200 transition-colors min-w-[40px] min-h-[36px] sm:min-w-[50px] sm:min-h-[40px] justify-center"
      >
        <Eye size={12} className="sm:w-3 sm:h-3" />
        <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.view')}</span>
        <span className="sm:hidden">👁</span>
      </button>

      {managerActionButtons}
    </div>
  );
});

PutAwayCardActions.displayName = 'PutAwayCardActions';

interface PutAwayCardProps {
  order: StcIntRelOrderDto;
  isOwned: boolean;
  /** Låst av en annen operatør — vises kun for lagersjef/administrator (profilgruppe 2/3) */
  isLockedByOther?: boolean;
  /** Lagersjef/administrator (profilgruppe 2/3) — viser slett/regenerer på ulåste ordrer */
  isManagerOrAdmin?: boolean;
  onStartPutAway: (order: StcIntRelOrderDto) => Promise<void>;
  onLockOrder: (stcOrderNo: number) => Promise<void>;
  onUnlockOrder: (stcOrderNo: number) => Promise<void>;
  onPreviewOrder: (stcOrderNo: number) => void;
  onDeleteOrder?: (order: StcIntRelOrderDto) => Promise<void>;
  onRegenerateOrder?: (order: StcIntRelOrderDto) => Promise<void>;
}

// Main card component with memoization
export const PutAwayCard: React.FC<PutAwayCardProps> = memo(({
  order,
  isOwned,
  isLockedByOther = false,
  isManagerOrAdmin = false,
  onStartPutAway,
  onLockOrder,
  onUnlockOrder,
  onPreviewOrder,
  onDeleteOrder,
  onRegenerateOrder
}) => {
  const { t } = useTranslation();
  
  const getPriorityColor = (priority: number) => {
    if (priority >= 900) return 'bg-red-500';
    if (priority >= 500) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return 'bg-gray-400';     // Ny
      case 10: return 'bg-green-500';   // Tildelt
      case 20: return 'bg-blue-500';    // Under arbeid
      case 30: return 'bg-yellow-500';  // Pauset
      case 100: return 'bg-green-600';  // Ferdig
      case 110: return 'bg-red-500';    // Avbrutt
      default: return 'bg-gray-400';    // Default
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return t('warehouse.modules.putaway.status.new');
      case 10: return t('warehouse.modules.putaway.status.assigned');
      case 20: return t('warehouse.modules.putaway.status.inProgress');
      case 30: return t('warehouse.modules.putaway.status.paused');
      case 100: return t('warehouse.modules.putaway.status.completed');
      case 110: return t('warehouse.modules.putaway.status.cancelled');
      default: return `${t('warehouse.modules.putaway.status.status')} ${status}`;
    }
  };

  const getTransTypeText = (transType: number) => {
    // StcIntRelOrder.TransType: 0=Intern flytting, 1=Utlegg, 2=Påfylling, 3=Optimalisering.
    // Samme kode-rom som backend (StcIntRelOrderController.GetTransTypeText, ADV-003).
    switch (transType) {
      case 0: return t('warehouse.modules.putaway.transTypes.internalMovement');
      case 1: return t('warehouse.modules.putaway.transTypes.putAway', {}, 'Utlegg');
      case 2: return t('warehouse.modules.putaway.transTypes.replenishment', {}, 'Påfylling');
      case 3: return t('warehouse.modules.putaway.transTypes.optimization', {}, 'Optimalisering');
      default: return `${t('warehouse.modules.putaway.transTypes.type')} ${transType}`;
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 p-2 sm:p-3 transition-all duration-200 ${
        isLockedByOther
          ? 'border-amber-200 bg-amber-50 hover:shadow-md hover:border-amber-300'
          : isOwned
          ? 'border-green-200 bg-green-50 hover:shadow-md hover:border-green-300'
          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
      }`}
    >
      {/* Mobile-first responsive layout */}
      <div className="space-y-2 sm:space-y-0">
        {/* Top row: Status dot + Order ID + Priority + Line count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${getStatusColor(order.orderStatus)}`} />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 flex-shrink-0">
              #{order.stcOrderNo}
            </h3>
            {isLockedByOther && order.lockedBy && (
              <span className="flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0">
                <Lock size={10} />
                {t('warehouse.modules.putaway.labels.lockedBy', { '0': order.lockedBy }, `Låst av ${order.lockedBy}`)}
              </span>
            )}
            {/* Description - truncated on mobile */}
            <div className="flex-1 min-w-0 px-1 sm:px-2">
              <div className="text-xs sm:text-sm text-gray-600 truncate">
                <span className="font-medium">{order.description1 || '-'}</span>
              </div>
            </div>
          </div>
          
          {/* Right side info - always visible but compact */}
          <div className="flex items-center gap-1 sm:gap-2 text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
            <Package size={12} className="text-gray-400" />
            <span className="font-medium">{order.lineCount || 0}</span>
            <span className="text-gray-500">P:{order.orderPriority}</span>
          </div>
        </div>

        {/* Second row: Additional info (mobile) / inline (desktop) */}
        <div className="flex items-center justify-between text-xs text-gray-500 sm:hidden">
          <div className="truncate">
            {getTransTypeText(order.transType)}
            {order.warehouseID && (
              <span className="ml-1">| {t('warehouse.general.warehouse')}: {order.warehouseID}</span>
            )}
          </div>
        </div>

        {/* Actions row */}
        <div className="flex items-center justify-end">
          <PutAwayCardActions
            order={order}
            isOwned={isOwned}
            isLockedByOther={isLockedByOther}
            isManagerOrAdmin={isManagerOrAdmin}
            onStartPutAway={onStartPutAway}
            onLockOrder={onLockOrder}
            onUnlockOrder={onUnlockOrder}
            onPreviewOrder={onPreviewOrder}
            onDeleteOrder={onDeleteOrder}
            onRegenerateOrder={onRegenerateOrder}
          />
        </div>

        {/* Desktop additional info - hidden on mobile */}
        <div className="hidden sm:block text-xs text-gray-500 mt-1">
          {getTransTypeText(order.transType)}
          {order.warehouseID && (
            <span className="ml-2">| {t('warehouse.general.warehouse')}: {order.warehouseID}</span>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Only re-render if essential props change, not loading states
  return (
    prevProps.order.stcOrderNo === nextProps.order.stcOrderNo &&
    prevProps.order.orderStatus === nextProps.order.orderStatus &&
    prevProps.order.lockedBy === nextProps.order.lockedBy &&
    prevProps.isOwned === nextProps.isOwned &&
    prevProps.isLockedByOther === nextProps.isLockedByOther &&
    prevProps.isManagerOrAdmin === nextProps.isManagerOrAdmin &&
    prevProps.order.orderPriority === nextProps.order.orderPriority &&
    prevProps.order.description1 === nextProps.order.description1 &&
    prevProps.order.lineCount === nextProps.order.lineCount
  );
});

PutAwayCard.displayName = 'PutAwayCard';
